import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Suspense } from 'react';
import blogApi from '@/api/blog';
import cmsApi from '@/api/cms';
import SEO from '@/components/ui/SEO';
import Loading from '@/components/ui/Loading';
import BlogPostCard from '@/components/blog/BlogPostCard';
import BlogPagination from '@/components/blog/BlogPagination';
import { BlockRenderer } from '@/components/BlockRenderer';
import { useLanguage } from '@/context/LanguageContext';

const BlogPage: React.FC = () => {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const activeTag = searchParams.get('tag') || undefined;
  const search = searchParams.get('search') || undefined;

  // Fetch CMS hero blocks for blog page
  const { data: cmsPage } = useQuery({
    queryKey: ['page', 'blog', language],
    queryFn: () => cmsApi.getPage('blog', language),
  });

  // Fetch blog posts
  const { data: postsResponse, isLoading: postsLoading } = useQuery({
    queryKey: ['blog', 'posts', { page, tag: activeTag, search, lang: language }],
    queryFn: () =>
      blogApi.getPosts({
        page,
        pageSize: 12,
        tag: activeTag,
        search,
        lang: language,
      }),
  });

  // Fetch tags for filter chips
  const { data: tags = [] } = useQuery({
    queryKey: ['blog', 'tags'],
    queryFn: () => blogApi.getTags(),
  });

  const posts = postsResponse?.data || [];
  const totalCount = postsResponse?.totalCount || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (searchInput.trim()) {
        params.set('search', searchInput.trim());
      } else {
        params.delete('search');
      }
      params.delete('page');
      return params;
    });
  };

  const toggleTag = (tagSlug: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (params.get('tag') === tagSlug) {
        params.delete('tag');
      } else {
        params.set('tag', tagSlug);
      }
      params.delete('page');
      return params;
    });
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchInput('');
  };

  const hasFilters = activeTag || search;

  return (
    <>
      <SEO
        title={cmsPage?.metaTitle || 'Blog'}
        description={cmsPage?.metaDescription || 'Latest news, updates, and insights from Dabar HR'}
        keywords={cmsPage?.metaKeywords}
        canonical={cmsPage?.canonicalUrl}
        ogImage={cmsPage?.ogImage}
        ogTitle={cmsPage?.ogTitle}
        ogDescription={cmsPage?.ogDescription}
        schemaMarkup={cmsPage?.schemaMarkup}
      />

      {/* CMS Hero Blocks */}
      {cmsPage?.blocks && cmsPage.blocks.length > 0 && (
        <Suspense fallback={<Loading fullScreen/>}>
          {cmsPage.blocks
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((block) => {
              if (!block.isVisible) return null;

              let parsedContent: any = {};
              let renderKey = '';

              try {
                if (typeof block.content === 'string') {
                  parsedContent = JSON.parse(block.content);
                } else if (typeof block.content === 'object') {
                  parsedContent = block.content;
                }
                renderKey = parsedContent?.blockRenderKey || '';
              } catch {
                renderKey = 'INVALID_JSON';
              }

              return (
                <div key={block.id} data-block-id={block.id}>
                  <BlockRenderer
                    data={parsedContent.data || {}}
                    blockType={block.blockType}
                    blockRenderKey={renderKey}
                  />
                </div>
              );
            })}
        </Suspense>
      )}

      <div className="section">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Search + Filters */}
          <div className="max-w-3xl mx-auto mb-12">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-6">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search blog posts..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
              />
              <button type="submit" className="btn btn-primary">
                Search
              </button>
            </form>

            {/* Tag Filter Chips */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.slug)}
                    className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                      activeTag === tag.slug
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {tag.name}
                    {tag.postCount > 0 && (
                      <span className="ml-1 opacity-60">({tag.postCount})</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Active Filters */}
            {hasFilters && (
              <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                <span>Filters:</span>
                {search && (
                  <span className="bg-gray-100 px-2 py-0.5 rounded">
                    Search: "{search}"
                  </span>
                )}
                {activeTag && (
                  <span className="bg-gray-100 px-2 py-0.5 rounded">
                    Tag: {tags.find((t) => t.slug === activeTag)?.name || activeTag}
                  </span>
                )}
                <button onClick={clearFilters} className="text-primary hover:underline ml-2">
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Blog Grid */}
          {postsLoading ? (
            <Loading />
          ) : posts.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              <p className="text-lg mb-2">No blog posts found.</p>
              {hasFilters && (
                <button onClick={clearFilters} className="text-primary hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>

              <BlogPagination page={page} pageSize={12} totalCount={totalCount} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;
